import axios from 'axios';
import { API_URL } from '../config/api';
import SessionService from './SessionService';

export interface EmailBroadcastItem {
  id: number;
  sectorId: number;
  name: string;
  subject: string;
  status: string;
  totalCount: number;
  sentCount: number;
  failedCount: number;
  createdAt: string;
  startedAt?: string;
  completedAt?: string;
  useAi?: boolean;
  aiAgentId?: number | null;
}

export interface EmailBroadcastDetail {
  broadcast: EmailBroadcastItem & { bodyHtml: string };
  recipients: Array<{
    id: number;
    emailContactId: number;
    email: string;
    status: string;
    sentAt?: string;
    errorMessage?: string;
    aiEnabled?: boolean;
  }>;
}

export interface EmailAttachmentRequest {
  fileName: string;
  contentBase64: string;
  contentType: string;
}

export interface CreateEmailBroadcastRequest {
  sectorId: number;
  name: string;
  subject: string;
  bodyHtml: string;
  emailContactIds: number[];
  cc?: string;
  bcc?: string;
  replyTo?: string;
  attachments?: EmailAttachmentRequest[];
  aiAgentId?: number | null;
}

const getAuthHeaders = () => {
  const token = SessionService.getToken();
  if (!token) throw new Error('Authentication token not found');
  return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
};

export const listEmailBroadcasts = async (
  sectorId: number,
  params?: { status?: string; page?: number; pageSize?: number }
): Promise<{ items: EmailBroadcastItem[]; totalCount: number; page: number; pageSize: number; totalPages: number }> => {
  const response = await axios.get(`${API_URL}/EmailBroadcast`, {
    params: { sectorId, ...params },
    headers: getAuthHeaders(),
  });
  const d = response.data;
  return {
    items: d?.items ?? [],
    totalCount: d?.totalCount ?? 0,
    page: d?.page ?? 1,
    pageSize: d?.pageSize ?? 20,
    totalPages: d?.totalPages ?? 0,
  };
};

export const getEmailBroadcast = async (id: number): Promise<EmailBroadcastDetail | null> => {
  const response = await axios.get(`${API_URL}/EmailBroadcast/${id}`, {
    headers: getAuthHeaders(),
  });
  return response.data;
};

export const createEmailBroadcast = async (dto: CreateEmailBroadcastRequest): Promise<EmailBroadcastItem> => {
  const response = await axios.post(`${API_URL}/EmailBroadcast`, dto, {
    headers: getAuthHeaders(),
  });
  return response.data;
};

export interface StartEmailBroadcastResult {
  status?: string;
  message?: string;
}

export const startEmailBroadcast = async (id: number): Promise<StartEmailBroadcastResult> => {
  const response = await axios.post<StartEmailBroadcastResult>(`${API_URL}/EmailBroadcast/${id}/start`, {}, {
    headers: getAuthHeaders(),
  });
  return response.data ?? {};
};

export const deleteEmailBroadcast = async (id: number): Promise<void> => {
  await axios.delete(`${API_URL}/EmailBroadcast/${id}`, {
    headers: getAuthHeaders(),
  });
};

export const toggleBroadcastAI = async (id: number, enabled: boolean): Promise<void> => {
  await axios.post(`${API_URL}/EmailBroadcast/${id}/toggle-ai`, { enabled }, {
    headers: getAuthHeaders(),
  });
};

export const toggleRecipientAI = async (broadcastId: number, recipientId: number, enabled: boolean): Promise<void> => {
  await axios.post(`${API_URL}/EmailBroadcast/${broadcastId}/recipients/${recipientId}/toggle-ai`, { enabled }, {
    headers: getAuthHeaders(),
  });
};
